import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { I18nSync } from "@/components/I18nSync";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthProvider";
import { ThemeProvider, useTheme } from "@/lib/ThemeContext";
import { signOut } from "firebase/auth";
import { getClientAuth } from "@/lib/firebase-client";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  ShoppingCart,
  Briefcase,
  Receipt,
  TrendingUp,
  FileBarChart,
  BarChart3,
  Settings,
  UserCircle,
} from "lucide-react";

interface NavItem { name: string; href: string; icon: LucideIcon; }
interface NavGroup { label: string; items: NavItem[] }

function buildNavGroups(t: (k: string) => string): NavGroup[] {
  return [
    {
      label: "Main",
      items: [
        { name: t("nav.dashboard"), href: "/dashboard", icon: LayoutDashboard },
        { name: t("nav.projects"),  href: "/projects",  icon: FolderKanban },
      ],
    },
    {
      label: "Finance",
      items: [
        { name: t("nav.expenses"), href: "/expenses", icon: Receipt },
        { name: t("nav.income"),   href: "/income",   icon: TrendingUp },
        { name: t("nav.sales"),    href: "/sales",    icon: FileBarChart },
        { name: t("nav.reports"),  href: "/reports",  icon: BarChart3 },
      ],
    },
    {
      label: "People",
      items: [
        { name: t("nav.farmers"), href: "/farmers", icon: Users },
        { name: t("nav.workers"), href: "/workers", icon: Briefcase },
        { name: t("nav.buyers"),  href: "/buyers",  icon: ShoppingCart },
      ],
    },
    {
      label: "Account",
      items: [
        { name: t("nav.profile"),   href: "/profile",   icon: UserCircle },
        { name: t("nav.settings"),  href: "/settings",  icon: Settings },
      ],
    },
  ];
}

/* ── Theme toggle segmented control ── */
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isJaggery = theme === "jaggery";

  const pill = (label: string, emoji: string, active: boolean, onClick: () => void) => (
    <button
      onClick={onClick}
      style={{
        padding: "4px 11px",
        borderRadius: 16,
        border: "none",
        cursor: active ? "default" : "pointer",
        fontSize: 11,
        fontWeight: 500,
        background: active ? "var(--tb-title)" : "transparent",
        color: active ? "#fff" : "var(--tb-icon)",
        transition: "all 0.2s",
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span>{emoji}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <div
      style={{
        display: "flex",
        border: "1px solid var(--tb-border)",
        borderRadius: 20,
        padding: 3,
        gap: 2,
        background: "transparent",
      }}
    >
      {pill("Light",   "☀",  !isJaggery, () => isJaggery  && toggleTheme())}
      {pill("Jaggery", "🌿", isJaggery,  () => !isJaggery && toggleTheme())}
    </div>
  );
}

/* ── Inner layout (needs ThemeContext) ── */
function DashboardLayoutInner() {
  const location   = useLocation();
  const navigate   = useNavigate();
  const pathname   = location.pathname;
  const { t }      = useTranslation();
  useTheme(); // keeps ThemeProvider active for child routes
  const { user, loading } = useAuth();

  const navGroups = buildNavGroups(t);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/signin");
  }, [user, loading, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(getClientAuth());
      navigate("/signin");
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--content-bg)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 44, height: 44, border: "4px solid hsl(var(--primary))", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: 13 }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const initials = user.displayName
    ? user.displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : (user.email?.charAt(0) ?? "U").toUpperCase();


  const getPageTitle = () => {
    const flat = navGroups.flatMap((g) => g.items);
    const found = flat.find((item) => item.href === pathname || (item.href !== "/dashboard" && pathname.startsWith(item.href + "/")));
    return found ? found.name : "JaggeryBook";
  };

  return (
    <div style={{ height: "100vh", display: "flex", overflow: "hidden", fontFamily: "'DM Sans', sans-serif" }}>
      <I18nSync />

      {/* Mobile backdrop */}
      {isSidebarOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }} onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* ── SIDEBAR ── */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-50 lg:static lg:z-auto transition-transform duration-200 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        style={{
          width: 220,
          background: "var(--sb-bg)",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div style={{ padding: "22px 20px 18px", borderBottom: "1px solid var(--sb-border)", flexShrink: 0 }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <div style={{ fontSize: 22, color: "#fff", fontWeight: 700, letterSpacing: "-0.3px", lineHeight: 1.1 }}>
              Jaggery<span style={{ color: "var(--sb-logo-accent)" }}>Book</span>
            </div>
            <div style={{ fontSize: 10, color: "var(--sb-section)", textTransform: "uppercase", letterSpacing: 1, marginTop: 3 }}>
              Business Platform
            </div>
          </Link>
        </div>

        {/* Nav groups */}
        <nav style={{ flex: 1, paddingBottom: 8 }}>
          {navGroups.map((group) => (
            <div key={group.label}>
              <div style={{ fontSize: 11, color: "var(--sb-section)", textTransform: "uppercase", letterSpacing: 1.5, padding: "16px 12px 8px", fontWeight: 600 }}>
                {group.label}
              </div>
              {group.items.map((item) => {
                const isActive = pathname === item.href || (item.href !== "/dashboard" && pathname.startsWith(item.href + "/"));
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsSidebarOpen(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 16px",
                      borderRadius: 8,
                      margin: "1px 8px",
                      color: isActive ? "var(--sb-active-text)" : "var(--sb-text)",
                      fontSize: 14,
                      fontWeight: isActive ? 500 : 400,
                      background: isActive ? "var(--sb-active-bg)" : "transparent",
                      textDecoration: "none",
                      transition: "background 0.15s, color 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.background = "var(--sb-hover-bg)";
                        (e.currentTarget as HTMLElement).style.color = "var(--sb-hover-text)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.background = "transparent";
                        (e.currentTarget as HTMLElement).style.color = "var(--sb-text)";
                      }
                    }}
                  >
                    <Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: 16, borderTop: "1px solid var(--sb-border)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--sb-avatar-bg)", color: "var(--sb-avatar-text)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: "var(--sb-user-name)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.displayName || "User"}
              </div>
              <div style={{ fontSize: 10, color: "var(--sb-user-email)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user.email}
              </div>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#E57373", fontSize: 12, padding: "4px 0", fontFamily: "inherit" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#ef4444"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "#E57373"; }}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div
        style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}
      >
        {/* Topbar */}
        <header
            style={{
              height: 56,
              flexShrink: 0,
              background: "var(--tb-bg)",
              borderBottom: "1px solid var(--tb-border)",
              display: "flex",
              alignItems: "center",
              padding: "0 20px",
              gap: 12,
              zIndex: 30,
              position: "sticky",
              top: 0,
            }}
          >
            {/* Mobile menu */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden"
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, color: "var(--tb-title)" }}
            >
              <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <h1 style={{ fontSize: 15, fontWeight: 600, color: "var(--tb-title)", margin: 0, flex: 1 }}>
              {getPageTitle()}
            </h1>

            <ThemeToggle />
          </header>

        {/* Content */}
        <main style={{ flex: 1, overflowY: "auto", background: "var(--content-bg)" }}>
          <div style={pathname === "/dashboard" ? { width: "100%" } : { maxWidth: 1400, margin: "0 auto", padding: 24 }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/* ── Public export wraps inner in ThemeProvider ── */
export default function DashboardLayout() {
  return (
    <ThemeProvider>
      <DashboardLayoutInner />
    </ThemeProvider>
  );
}
